/**
 * Media Header Component
 *
 * Provides controls for upload, search, and sorting in the asset browser view.
 */

import { useMemo, useState } from 'react';
import { SortOption } from '../types';
import { getEmbedTemplates } from '../embedTemplates';
import { FindIcon, SettingsIcon, CrossIcon, TileIcon, ListIcon, UploadIcon, OpenInNewIcon, ArrowBackIcon } from '../../../icons';
import type { Filters, MediaType } from '../types';
import getAcceptedFileTypes from '../utils';
import { useMode } from '../context/ModeContext';

import { useAssetActions } from '../context/AssetActionsContext';
import { useLabels, useWorkflowStatuses } from '../hooks';
import { SortDropdown } from './SortDropdown';
import { Dropzone } from './Dropzone';
import { ZprMultiSelect } from '@zpr-stencil';
import { MultiSelectOption } from '@zappar/zpr-ui-components-stencil';
import { useTheme } from '../context/ThemeContext';

export interface MediaHeaderProps {
  /** Current search query */
  searchQuery: string;
  /** Current sort option */
  sortBy: SortOption;
  /** Current filters */
  filters: Filters;
  /** Callback for search query changes */
  onSearchChange: (query: string) => void;
  /** Callback for sort option changes */
  onSortChange: (sortBy: SortOption) => void;
  /** Callback for filters changes */
  onFiltersChange: (filters: Filters) => void;
  /** Callback for file upload */
  onUpload?: (files: FileList) => void;
  /** Callback for view toggle (table/grid) */
  onViewToggle?: (isTableView: boolean) => void;
  /** Whether the current view is table view (if applicable) */
  isTableView?: boolean;
  /** Show animated filter (if applicable) */
  showAnimatedFilter?: boolean;
  /** Accepted file types for upload input (e.g., "image/*", "video/*") */
  acceptedFileTypes?: MediaType[];
  /** Whether to show the multi-select toggle control */
  showMultiSelectToggle?: boolean;
  /** Enable multi-selection mode (if applicable) */
  enableMultiSelect?: boolean;
  /** Callback to toggle multi-selection mode */
  onToggleMultiSelect?: () => void;
  /** Callback to open premium features modal */
  onOpenPremiumUploadModal?: () => void;
  /** Callback to go back to the previous view */
  onBack?: () => void;
  currentViewTitle?: string;
}

export const MediaHeader = ({
  searchQuery,
  sortBy,
  filters,
  onSearchChange,
  onSortChange,
  onFiltersChange,
  onUpload,
  onViewToggle,
  isTableView,
  acceptedFileTypes = [],
  showAnimatedFilter = false,
  showMultiSelectToggle = false,
  enableMultiSelect = false,
  onToggleMultiSelect,
  onOpenPremiumUploadModal,
  onBack,
  currentViewTitle,
}: MediaHeaderProps) => {
  const { zapworksProvider, workspaceId, onCreateLabel, onOpenMediaLibraryModal } = useAssetActions();
  const { labels: availableLabels, createLabel } = useLabels(zapworksProvider, workspaceId);
  const { statuses: workflowStatuses } = useWorkflowStatuses(zapworksProvider, workspaceId);

  const [showFilters, setShowFilters] = useState(true);
  const { premium, isToolMode, permissions, useNestleFeatures } = useMode();
  const theme = useTheme();

  const statusOptions: MultiSelectOption[] = workflowStatuses.map(s => ({
    value: s.uid,
    label: s.name,
    selected: filters.status.includes(s.uid)
  }));

  const templateTypeOptions: MultiSelectOption[] = [{
    value: 'unpublished',
    label: 'Unpublished',
    selected: filters.templateType.includes('unpublished') || false
  }].concat(getEmbedTemplates({ features: { useNestleFeatures } }).map(option => ({
    value: option.value,
    label: option.label,
    selected: filters.templateType.includes(option.value) || false
  })));

  // Generate labelOptions dynamically from available labels
  const labelOptions: MultiSelectOption[] = availableLabels.map(label => ({
    value: label.uid,
    label: label.name.charAt(0).toUpperCase() + label.name.slice(1), // Capitalize first letter
    selected: filters.labels.includes(label.uid),
    color: label.color // Pass color for label styling in dropdown
  }));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0 && onUpload) {
      onUpload(files);
      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  const handleFilterSettingsClick = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (filterKey: string, values: string[]) => {
    onFiltersChange({
      ...filters,
      [filterKey]: values
    });
  };

  const handleClearAllFilters = () => {
    onFiltersChange({
      animated: [],
      templateType: [],
      status: [],
      labels: [],
    });
  };

  const handleViewToggleClick = () => {
    if (onViewToggle) {
      onViewToggle(!isTableView);
    }
  };

  const totalSelectedFilters = filters.animated.length + filters.templateType.length + filters.status.length + filters.labels.length;
  const showClearButton = totalSelectedFilters > 0;
  const title = useMemo(() => { 
    if (onBack && currentViewTitle) {
      return currentViewTitle;
    } else {
      return 'Assets';
    }
   }, [currentViewTitle]);

  return (
    <div className="media-header-wrapper">
      <header className="media-header">
        { isToolMode && onOpenMediaLibraryModal && (
            <div className="xs:display-block display-none media-header__tool-mode horizon-theme">
                <h2 className="flex ai-center jc-start">{onBack && <button className="btn btn-ghost btn-medium p0" onClick={onBack}><ArrowBackIcon /></button>} {title}</h2>
                <button
                    className="btn btn-secondary btn-medium p0 w100"
                    aria-label="Open media library"
                    onClick={onOpenMediaLibraryModal}
                >
                    <OpenInNewIcon />
                    Open Media Library
                </button>
            </div>
        )}
        <div className="media-header__controls">
            {onUpload && (
            <Dropzone onUpload={onUpload} acceptedFileTypes={acceptedFileTypes} fitToContent>
                <label className="media-header__upload-btn btn btn-medium" aria-label="Upload new assets">
                <span className="xs:display-none">Upload</span>
                <span className="display-none xs:display-flex"><UploadIcon className="m0"/></span>
                {
                    !premium ? (
                    <input
                        type="file"
                        multiple
                        accept={acceptedFileTypes.map(type => getAcceptedFileTypes(type)).join(',')}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        aria-label="Select files to upload"
                    />
                    ) : (
                    <button
                        onClick={onOpenPremiumUploadModal}
                        style={{ display: 'none' }}
                    ></button>
                    )
                }
                </label>
            </Dropzone>
            )}

            <div className="media-header__search">
            {!searchQuery && <FindIcon className="media-header__search-icon" />}
            {showClearButton && (
                <button
                type="button"
                className="media-header__search-label"
                aria-label="Clear filters"
                onClick={handleClearAllFilters}
                >
                <span>{totalSelectedFilters} filters</span>
                <CrossIcon />
                </button>
            )}
            <input
                type="search"
                name="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                className={`media-header__search-input ${showClearButton ? 'with-label' : ''}`}
                aria-label="Search assets"
            />
            </div>

            <button
            className={`btn btn-ghost p0 ${showFilters ? 'active' : ''}`}
            aria-label="Filter settings"
            title={showFilters ? 'Hide filter settings' : 'Show filter settings'}
            id="media-header__filter-settings-button"
            onClick={handleFilterSettingsClick}
            >
            <SettingsIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
            </button>

            {onViewToggle && !isToolMode && (
            <button
                className={`btn btn-ghost p0`}
                aria-label="Toggle view settings"
                title={isTableView ? 'Switch to grid view' : 'Switch to table view'}
                onClick={handleViewToggleClick}
            >
                {isTableView ? <TileIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" /> : <ListIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />}
            </button>
            )}

            {showMultiSelectToggle && (
            <button
                type="button"
                className={`btn btn-small btn-secondary ${enableMultiSelect ? 'active' : ''}`}
                aria-label={enableMultiSelect ? 'Disable multi-selection mode' : 'Enable multi-selection mode'}
                title={enableMultiSelect ? 'Disable multi-selection mode' : 'Enable multi-selection mode'}
                onClick={onToggleMultiSelect}
            >
                Select
            </button>
            )}
        </div>
      </header>

      {showFilters && (
        <section className="media-header__filters-section">
          <div className="media-header__filters-container">
            {/* Animated Filter */}
            {showAnimatedFilter && (
              <div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="Animated"
                    checked={filters.animated.includes('true')}
                    onChange={(e) => handleFilterChange('animated', e.target.checked ? ['true'] : [])}
                  />
                  <span>Animated</span>
                </label>
              </div>
            )}

            {/* Status Filter : Premium Feature */}
            {premium && !isToolMode && (
              <ZprMultiSelect
                placeholder="By status"
                heading="Status"
                options={statusOptions}
                onSelectionchanged={(e) => handleFilterChange('status', e.detail)}
                theme={theme}
                dropdownZIndex="1001"
              />
            )}

            {/* Labels Filter */}
            <ZprMultiSelect
              placeholder="By labels"
              heading="Labels"
              options={labelOptions}
              isLabelOptions={true}
              enableSearch
              onSelectionchanged={(e) => handleFilterChange('labels', e.detail)}
              creatable={permissions?.canEditLabels}
              onCreateOption={(e) => createLabel({ name: e.detail, color: '#EAEAEA' }).then(() => onCreateLabel?.(e.detail))}
              theme={theme}
              dropdownZIndex="1001"
            />

            {/* Template Type Filter : Premium Feature */}
            {premium && !isToolMode && (
              <ZprMultiSelect
                placeholder="By published template"
                heading="Published template"
                options={templateTypeOptions}
                onSelectionchanged={(e) => handleFilterChange('templateType', e.detail)}
                theme={theme}
                dropdownZIndex="1001"
              />
            )}

            {/* Clear all button */}
            {showClearButton && (
              <button
                type="button"
                aria-label="Clear all filters"
                className="btn btn-small btn-link"
                onClick={handleClearAllFilters}
              >
                Clear all
                <span>({totalSelectedFilters})</span>
              </button>
            )}

          </div>

          {/* Sort dropdown */}
          <SortDropdown
            sortBy={sortBy}
            onSortChange={onSortChange}
            theme={theme}
            dropdownZIndex="1001"
          />
        </section>
      )}
    </div>
  );
};